import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  Pencil, Trash2, Plus, Loader2, Search, ChevronLeft, Scroll,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { adminApi, prophetApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { GlassCard, PageLoading } from '../components/shared';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { ProphetForm } from './AdminDashboard';

const AdminProphets = () => {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | prophetObj
  const [confirmDelete, setConfirmDelete] = useState(null); // null | prophetObj
  const [deleting, setDeleting] = useState(false);

  const fetchProphets = useCallback(async () => {
    setLoading(true);
    try {
      const r = await prophetApi.getProphets();
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') fetchProphets();
  }, [user, fetchProphets]);

  const handleSaved = async () => {
    setEditing(null);
    await fetchProphets();
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await adminApi.deleteProphet(confirmDelete.id || confirmDelete._id);
      toast.success(`${confirmDelete.name_english || 'Prophète'} supprimé`);
      setConfirmDelete(null);
      await fetchProphets();
    } catch (e) {
      toast.error('Échec de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  if (authLoading) return <PageLoading />;
  if (!user || user.role !== 'admin') return <Navigate to="/login" replace />;

  const q = search.trim().toLowerCase();
  const filtered = q
    ? items.filter((p) => {
        const hay = [
          p.name_english, p.name_french, p.name_arabic, p.era, p.location,
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      })
    : items;

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
          <div>
            <Link to="/admin" className="inline-flex items-center text-sm text-text-muted hover:text-primary mb-2" data-testid="admin-prophets-back-link">
              <ChevronLeft className="w-4 h-4 mr-1" /> Retour au tableau de bord
            </Link>
            <h1 className="text-3xl font-serif text-text-primary flex items-center gap-2">
              <Scroll className="w-7 h-7 text-purple-400" />
              Gestion des prophètes
            </h1>
            <p className="text-text-secondary mt-1">{items.length} prophète{items.length > 1 ? 's' : ''} enregistré{items.length > 1 ? 's' : ''}</p>
          </div>
          <Button
            className="bg-primary text-background"
            onClick={() => setEditing('new')}
            data-testid="admin-prophets-add-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Ajouter un prophète
          </Button>
        </div>

        {/* Search */}
        <GlassCard className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <Input
              placeholder="Rechercher par nom, époque, lieu…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-surface border-border"
              data-testid="admin-prophets-search-input"
            />
          </div>
        </GlassCard>

        {/* List */}
        {loading ? (
          <PageLoading />
        ) : filtered.length === 0 ? (
          <GlassCard className="text-center py-12 text-text-muted">
            {q ? 'Aucun prophète ne correspond à votre recherche.' : 'Aucun prophète enregistré.'}
          </GlassCard>
        ) : (
          <div className="space-y-3" data-testid="admin-prophets-list">
            {filtered.map((p) => (
              <GlassCard
                key={p.id || p._id}
                className="flex items-start justify-between gap-4 flex-wrap"
                data-testid={`admin-prophet-row-${p.id || p._id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono text-text-muted">#{p.order ?? '–'}</span>
                    <h3 className="font-serif text-lg text-text-primary">
                      {p.name_french || p.name_english}
                    </h3>
                    {p.name_arabic && (
                      <span className="font-arabic text-lg text-primary" dir="rtl">{p.name_arabic}</span>
                    )}
                    {p.certainty_level && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-surface-elevated text-text-muted border border-border">
                        {p.certainty_level}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-text-secondary mt-1 line-clamp-2">
                    {p.summary
                      ? <span>{p.summary.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 200)}</span>
                      : <span className="italic text-text-muted">Pas de résumé</span>}
                  </p>
                  <div className="flex gap-3 mt-2 text-xs text-text-muted">
                    {p.era && <span>📅 {p.era}</span>}
                    {p.location && <span>📍 {p.location}</span>}
                    {Array.isArray(p.quran_mentions) && p.quran_mentions.length > 0 && (
                      <span>📖 {p.quran_mentions.length} référence{p.quran_mentions.length > 1 ? 's' : ''}</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing(p)}
                    data-testid={`admin-prophet-edit-${p.id || p._id}`}
                  >
                    <Pencil className="w-4 h-4 mr-1" /> Modifier
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-rose-500/40 text-rose-400 hover:bg-rose-500/10"
                    onClick={() => setConfirmDelete(p)}
                    data-testid={`admin-prophet-delete-${p.id || p._id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </GlassCard>
            ))}
          </div>
        )}

        {/* Edit / Create dialog */}
        <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-prophet-form-dialog">
            <DialogHeader>
              <DialogTitle>
                {editing === 'new' ? 'Ajouter un prophète' : `Modifier ${editing?.name_french || editing?.name_english || ''}`}
              </DialogTitle>
              <DialogDescription>
                Les champs marqués d'une * sont obligatoires.
              </DialogDescription>
            </DialogHeader>
            {editing && (
              <ProphetForm
                initial={editing === 'new' ? null : editing}
                onSuccess={handleSaved}
                onCancel={() => setEditing(null)}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Delete confirm */}
        <AlertDialog open={!!confirmDelete} onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
          <AlertDialogContent data-testid="admin-prophet-delete-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer ce prophète ?</AlertDialogTitle>
              <AlertDialogDescription>
                Vous êtes sur le point de supprimer <strong>{confirmDelete?.name_french || confirmDelete?.name_english}</strong>.
                Cette action est irréversible. Les liens vers les versets du Coran (quran_mentions) seront perdus.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="admin-prophet-delete-confirm-btn"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Supprimer définitivement
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default AdminProphets;
